import { PetDetails, PetVaccine } from '@weave/schema-gen-ts/dist/schemas/pets-api/v1/pets_api.pb';
import { isBefore } from 'date-fns';

type ServicesRecord = Record<
  PetVaccine['description'],
  {
    eventId: PetVaccine['eventId'];
    eventTime: PetVaccine['eventTime'];
    eventStatus: PetVaccine['eventStatus'];
  }
>;

/**
 * Sorts the pet vaccines by their event time
 *
 * @param serviceRecordList - The pet vaccine list to sort
 * @param order - The sorting order. Use 'asc' for ascending or 'desc' for descending
 * @returns vaccines sorted by order
 */
const sortServiceRecordList = (serviceRecordList: PetVaccine[], order: 'asc' | 'desc') =>
  serviceRecordList.sort((a, b) =>
    order === 'asc' ? a?.eventTime.localeCompare(b.eventTime!) : b?.eventTime.localeCompare(a.eventTime!)
  );

export const getTransformedPetServices = (petExtended?: PetDetails) => {
  if (!petExtended) {
    return { overdueServices: [], upcomingServices: [] };
  }
  const data = petExtended;
  // create a record of unique services based on service description
  // if there are duplicates, only keep the most recent one
  const dedupedByDescription = () => {
    const dedupedByDescription: ServicesRecord = {};
    data?.vaccines?.filter((currentService) => {
      const recordServiceKey = currentService.description;
      // if we haven't seen this key yet, add it to the record
      if (!dedupedByDescription[recordServiceKey]) {
        dedupedByDescription[recordServiceKey] = {
          eventId: currentService.eventId,
          eventTime: currentService.eventTime,
          eventStatus: currentService.eventStatus,
        };
      } else {
        // if we have seen it already, check if it is more recent than the existing instance
        const existingDueService = dedupedByDescription[recordServiceKey];
        const currentIsMoreRecent = isBefore(
          new Date(existingDueService.eventTime),
          new Date(currentService.eventTime)
        );
        // if it is more recent, replace the older item in the record
        if (currentIsMoreRecent) {
          dedupedByDescription[recordServiceKey] = {
            eventId: currentService.eventId,
            eventTime: currentService.eventTime,
            eventStatus: currentService.eventStatus,
          };
        }
      }
    });

    return dedupedByDescription;
  };

  // convert the record back into an array
  const dedupedServiceArray = Object.entries(dedupedByDescription()).map(([description, service]) => ({
    eventId: service.eventId,
    eventTime: service.eventTime,
    eventStatus: service.eventStatus,
    description,
  }));

  // split the services into past and future
  const dueServices = dedupedServiceArray.filter((service) => isBefore(new Date(service.eventTime), new Date()));
  const upcomingServices = dedupedServiceArray.filter((service) => isBefore(new Date(), new Date(service.eventTime)));

  return {
    overdueServices: sortServiceRecordList(dueServices, 'desc'),
    upcomingServices: sortServiceRecordList(upcomingServices, 'asc'),
  };
};
